import React, { useState, useEffect, Fragment } from 'react';
import { Icon, Select, Divider, Spinner } from 'selfly-ui-react';
import validator from 'validator';

import useCurrentUser from '../../services/User/UserHooks';

import { JOKI } from '../../JOKI';
import MySettings from './views/mySettings';
import ViewToggler from './../ViewToggler/ViewToggler';
import OtherUsers from './views/otherUsers';

import './styles.scss';

const AlarmSettings = ({ isOpen, onClose, permissions }) => {
    const user = useCurrentUser();

    const [activeOrganisation, setActiveOrganisation] = useState({});
    const [userView, setUserView] = useState('MySettings');
    const [allOrganisations, setAllOrganisations] = useState([]);
    const [locations, setlocations] = useState([]);

    const [alertSettings, setAlertSettings] = useState({});
    const [configResponse, setConfigResponse] = useState(null);

    const [dataSaved, setDataSaved] = useState(false);
    const [hasError, setHasError] = useState({ status: false, message: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [mySettingsConfig, setMySettingsConfig] = useState({});
    const [otherUsersSettingsConfig, setOtherUsersSettingsConfig] = useState([]);

    //the permission to manage configuration
    const hasPermission = permissions.includes('alertNotificationSettings');

    // fetch logged-in user and set all the organisations
    useEffect(() => {
        // return if the user is not loaded yet
        if (!user || Object.keys(user).length <= 0) {
            return;
        }

        // get all Organisations
        const orgs = user.groups;
        setAllOrganisations(
            orgs.map(org => {
                return {
                    label: org.Name,
                    value: org.Id,
                };
            })
        );
    }, [user]);

    // Request JOKi to fetch all the location names
    useEffect(() => {
        if (!activeOrganisation.value) {
            return;
        }
        JOKI.ask({
            from: 'AlertSettings:locations',
            to: 'LocationService',
            key: 'getLocationNames',
            body: activeOrganisation.value,
        }).then(results => {
            // just ask for the JOKI for the service
        });
    }, [activeOrganisation]);

    useEffect(() => {
        JOKI.on({
            from: 'LocationService',
            key: 'serviceUpdate',
            fn: event => {
                if (event.body) {
                    setlocations(event.body);
                }
            },
        });
    });

    // when organisation changes, refetch the alert
    // configurations from the database
    useEffect(() => {
        if(activeOrganisation && activeOrganisation.value) {
            setIsLoading(true);
        }
        //reload the alert config
        JOKI.trigger({
            from: 'React:AlertConfigCreatedOrUpdatedOrDeletedOrOrgChange',
            key: 'alertConfigRefetch',
            body: { key: 'organization', value: activeOrganisation, type: 'string' },
        });
    }, [activeOrganisation]);

    // when alert configuration is fetched, created or updated,
    // set configuration to populate on the UI
    useEffect(() => {
        return JOKI.on({
            from: 'AlarmSettingService',
            key: 'serviceUpdate',
            fn: event => {
                if (event.body) {
                    setIsLoading(false);
                    setAlertSettings(event.body.alertConfig);
                    setConfigResponse(event.body.configResponse);
                }
            },
        });
    });

    //once the alert config is updated/created, refecth the alert config
    useEffect(() => {
        if (configResponse && configResponse.configurationId && !configResponse.hasOwnProperty('message')) {
            setDataSaved(true);
        } else {
            setHasError({ status: true, message: 'Error: Changes not saved.' });
            console.error(configResponse);
        }

        setIsSaving(false);

        //set this timeout for notification to appear and disapper
        setTimeout(() => {
            setDataSaved(false);
        }, 5000);

        //reload the alert config
        JOKI.trigger({
            from: 'React:AlertConfigCreatedOrUpdatedOrDeletedOrOrgChange',
            key: 'alertConfigRefetch',
            body: { key: 'organization', value: activeOrganisation, type: 'string' },
        });

        setHasError({ status: false, message: '' });
    }, [configResponse]);

    // when alertSettings is ready, set severities, alertTypes and receivers
    // configId for current user config from database
    useEffect(() => {
        if (alertSettings.organization) {
            if (alertSettings.organization.length) {
                const alertConfigurations = alertSettings.organization[0].alertConfigurations;

                if (!alertConfigurations || alertConfigurations.length === 0) {
                    setMySettingsConfig({});
                    setOtherUsersSettingsConfig([]);
                    return;
                }

                const mySettings = alertConfigurations.find(setting => setting.userName === user.email);
                const otherUsersSettings = alertConfigurations.filter(setting => setting.userName !== user.email);

                if (mySettings) {
                    setMySettingsConfig(mySettings);
                } else {
                    setMySettingsConfig({});
                }

                if (otherUsersSettings) {
                    setOtherUsersSettingsConfig(otherUsersSettings);
                } else {
                    setOtherUsersSettingsConfig([]);
                }
            }
        } else {
            setMySettingsConfig({});
            setOtherUsersSettingsConfig([]);
        }
    }, [alertSettings]);

    // save alert settings
    const saveChanges = payload => {
        let alarmPayload = { ...payload };
        const settingId = alarmPayload.id;

        // check for reveivers email validity before saving
        const emailInvalid = alarmPayload.receivers
            .map(email => email.value)
            .some(email => {
                return !validator.isEmail(email);
            });

        if (emailInvalid) {
            setHasError({ status: true, message: "Error: Receiver's email should be valid." });
            //set this timeout for notification to appear and disapper
            setTimeout(() => {
                setHasError({ status: false, message: '' });
            }, 5000);

            return;
        } else {
            setHasError({ status: false, message: '' });
        }

        alarmPayload = {
            ...alarmPayload,
            receivers: alarmPayload.receivers.map(email => email.value),
            alertTypes: alarmPayload.alertTypes.map(alert => {
                return { type: alert.type, severity: alert.severity };
            }),
            isActive: true,
        };

        // if configId is null, create a new alertConfiguration
        // otherwise, update the alertConfiguation for given configId
        if (!settingId) {
            JOKI.trigger({
                to: 'AlarmSettingService',
                key: 'create_alert_config',
                body: {
                    payload: { ...alarmPayload, actionType: 'email' },
                },
            });
        } else {
            JOKI.trigger({
                to: 'AlarmSettingService',
                key: 'update_alert_config',
                body: {
                    payload: alarmPayload,
                    configId: settingId,
                },
            });
            setConfigResponse(null);
        }
        setIsLoading(true);
    };

    const deleteConfig = settingId => {
        JOKI.trigger({
            to: 'AlarmSettingService',
            key: 'delete_alert_config',
            body: {
                configId: settingId,
            },
        });

        setConfigResponse(null);
        setIsLoading(true);
    };

    return (
        <div className={`settings ${!isOpen ? 'settings--close' : ''}`}>
            <div className="settings__header">
                <div className="settings__header-title">
                    <Icon>access_alarm</Icon> Alarm Notification Settings
                </div>
                <div className="settings__header-icon" onClick={() => onClose(false)}>
                    <Icon>close</Icon>
                </div>
            </div>
            <div className="settings__content">
                <div className="settings__org">
                    <div className="settings__org-select">
                        <div className="settings__item-label"> Select an organization</div>
                        <Select options={allOrganisations} onChange={org => setActiveOrganisation(org)} />
                    </div>
                </div>

                <Divider />

                {Object.keys(activeOrganisation).length === 0 && (
                    <div className="settings__item-label">Please select your organization to configure alarm notifications</div>
                )}

                {(isLoading || isSaving) && <Spinner />}

                {Object.keys(activeOrganisation).length > 0 && !isLoading && !isSaving && (
                    <Fragment>
                        {hasPermission && (
                            <div className="settings__view-toggle">
                                <ViewToggler
                                    size="md"
                                    onChange={direction => (direction === 'left' ? setUserView('MySettings') : setUserView('OtherUsers'))}
                                    leftTitle="My Settings"
                                    rightTitle="Other Users"
                                    value={userView === 'MySettings' ? 'left' : 'right'}
                                />
                            </div>
                        )}

                        {hasError.status && <div className="settings__notification settings__notification--error">{hasError.message}</div>}
                        {dataSaved && (
                            <div className="settings__notification settings__notification--success">
                                <Icon>check</Icon>&nbsp; Done !
                            </div>
                        )}

                        <div>
                            {userView === 'MySettings' && (
                                <MySettings saveChanges={saveChanges} deleteConfig={deleteConfig} mySettings={mySettingsConfig} locations={locations} />
                            )}
                            {userView === 'OtherUsers' && hasPermission && (
                                <OtherUsers
                                    alarmSettings={otherUsersSettingsConfig}
                                    locations={locations}
                                    saveChanges={saveChanges}
                                    deleteConfig={deleteConfig}
                                />
                            )}
                        </div>
                    </Fragment>
                )}
            </div>
        </div>
    );
};

export default AlarmSettings;
